import { OrderDetailStatus } from '@api/enterprise/openapi/models';

import { COL_LETTERS, RACK_COLS, RACK_ROWS } from '../constants';

import type { WorkOrderDetailViewModel } from '@api/enterprise/openapi/models';
import type { CellState, GridPosition, RackCell, RackStats } from '../types';

export const sequenceToPosition = (seq: number): GridPosition => ({
  row: ((seq - 1) % RACK_ROWS) + 1,
  col: Math.ceil(seq / RACK_ROWS)
});

export const positionToSequence = (row: number, col: number): number =>
  (col - 1) * RACK_ROWS + row;

export const formatPositionLabel = (seq: number): string => {
  const { row, col } = sequenceToPosition(seq);
  const colLetter = COL_LETTERS[col - 1] ?? '?';
  return `${colLetter}${String(row).padStart(2, '0')}`;
};

export const mapDetailToCellState = (
  detail: WorkOrderDetailViewModel | null
): CellState => {
  if (!detail || !detail.status) return 'empty';

  switch (detail.status) {
    case OrderDetailStatus.Incomplete:
    case OrderDetailStatus.Backordered:
      return 'assigned';
    case OrderDetailStatus.Complete:
      return 'complete';
    case OrderDetailStatus.Skipped:
      return 'skipped';
    case OrderDetailStatus.Unknown:
    case OrderDetailStatus.UnknownDefaultOpenApi:
    default:
      return 'empty';
  }
};

export const buildRackGrid = (
  details: WorkOrderDetailViewModel[]
): RackCell[][] => {
  const grid: RackCell[][] = [];

  for (let row = 1; row <= RACK_ROWS; row++) {
    const rowCells: RackCell[] = [];
    for (let col = 1; col <= RACK_COLS; col++) {
      const seq = positionToSequence(row, col);
      const detail = details.find(d => d.sequenceNumber === seq) ?? null;

      rowCells.push({
        position: { row, col },
        sequenceNumber: seq,
        detail,
        cellState: mapDetailToCellState(detail)
      });
    }
    grid.push(rowCells);
  }

  return grid;
};

export const flattenGrid = (grid: RackCell[][]): RackCell[] => grid.flat();

export const computeSummaryStats = (cells: RackCell[]): RackStats => {
  const stats: RackStats = {
    empty: 0,
    assigned: 0,
    'in-progress': 0,
    complete: 0,
    failed: 0,
    skipped: 0
  };

  cells.forEach(cell => {
    stats[cell.cellState]++;
  });

  return stats;
};
